/**
 * `OnboardingHero` — adaptive top-of-dashboard card that surfaces the right
 * next-step CTA based on tenant state. Replaces a multi-step onboarding
 * wizard with a single contextual hero that disappears once the tenant has
 * a subscription, an agent, and a run.
 *
 * State machine (first match wins):
 *   1. `subscription.status` not in {active, trialing} → Subscribe card.
 *   2. No agents yet → "Pick your starting point" with three tiles.
 *   3. Agents exist but no runs yet → "Run your first agent" CTA pointing
 *      at the most recently created agent.
 *   4. Otherwise → render nothing (the regular module summary cards are
 *      sufficient signal once the tenant is rolling).
 *
 * Decision (2026-05-09): explicitly NOT a multi-step wizard. Per Tony's
 * scoping conversation, agents only deliver value with connections wired,
 * so a generic "first agent run" wizard would feel hollow. This hero
 * nudges in the right direction without forcing a flow.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Plug, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAgentsList } from "@/hooks/use-agents";
import { useRunsList } from "@/hooks/use-runs";
import { useSubscription } from "@/hooks/use-billing";
import type { AgentResponse } from "@/lib/api-client";

const ENTITLED_STATUSES = new Set(["active", "trialing"]);

export function OnboardingHero() {
  const subscription = useSubscription();
  const agents = useAgentsList({ limit: 50 });
  // Match the dashboard's runs query params so React Query dedupes — only
  // one network call between the two consumers.
  const runs = useRunsList({ limit: 10 });

  // While we're still loading the signals, render nothing — better empty
  // than flash-of-wrong-CTA. Also bail when any signal is missing data
  // (network error / unmocked-in-tests) so we don't render a wrong-default
  // hero on a transient failure.
  if (subscription.isLoading || agents.isLoading || runs.isLoading) return null;
  if (!subscription.data || !agents.data || !runs.data) return null;

  const subStatus = subscription.data.status;

  // Stage 1 — unsubscribed. Highest-priority next step.
  if (!ENTITLED_STATUSES.has(subStatus)) {
    return <SubscribePromptCard />;
  }

  const agentItems = agents.data.items;
  if (agentItems.length === 0) {
    return <PickStartingPointCard />;
  }

  const runItems = runs.data.items;
  if (runItems.length === 0) {
    // Sort by created_at descending so we point at whatever they just made.
    const mostRecent = [...agentItems].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];
    if (mostRecent) {
      return <FirstRunCard agent={mostRecent} />;
    }
  }

  return null;
}

function SubscribePromptCard() {
  return (
    <Card data-testid="onboarding-subscribe-card" className="border-primary bg-primary/5">
      <CardHeader>
        <CardTitle>Welcome to Relay — pick a plan to get started</CardTitle>
        <CardDescription>
          Agent runs require an active subscription. Plans start at $99/mo for 2,000,000 Relay
          Credits — enough for hundreds of light agent tasks.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        <Link to="/settings/billing">
          <Button data-testid="onboarding-subscribe-cta">
            View plans <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>
        <Link
          to="/settings/billing"
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          What are credits?
        </Link>
      </CardContent>
    </Card>
  );
}

function PickStartingPointCard() {
  return (
    <Card data-testid="onboarding-starting-point-card">
      <CardHeader>
        <CardTitle>Pick your starting point</CardTitle>
        <CardDescription>
          Three good first moves. Connecting your tools first usually pays off — Relay's value
          shows up once an agent can actually act on your data.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StartingTile
          to="/connections"
          icon={<Plug className="h-5 w-5" />}
          title="Connect your tools"
          body="Twilio, Gmail, social OAuth, calendar, etc."
          testId="onboarding-tile-connections"
        />
        <StartingTile
          to="/agents/templates"
          icon={<Sparkles className="h-5 w-5" />}
          title="Browse agent templates"
          body="Pre-built agents for common SMB tasks."
          testId="onboarding-tile-templates"
        />
        <StartingTile
          to="/settings/billing"
          icon={<ArrowRight className="h-5 w-5" />}
          title="Read the credits FAQ"
          body="How allowance, multipliers, and top-ups work."
          testId="onboarding-tile-faq"
        />
      </CardContent>
    </Card>
  );
}

function StartingTile({
  to,
  icon,
  title,
  body,
  testId,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  body: string;
  testId: string;
}) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 text-left transition-colors hover:border-primary hover:bg-accent/30"
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <span className="text-primary">{icon}</span>
        {title}
      </span>
      <span className="text-xs text-muted-foreground">{body}</span>
    </Link>
  );
}

function FirstRunCard({ agent }: { agent: AgentResponse }) {
  return (
    <Card data-testid="onboarding-first-run-card" className="border-primary bg-primary/5">
      <CardHeader>
        <CardTitle>Run your first agent</CardTitle>
        <CardDescription>
          You created <span className="font-semibold">{agent.name}</span> but haven't dispatched a
          run yet. Try it out — see the credit cost and output in real time.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Link to={`/agents/${agent.id}`}>
          <Button data-testid="onboarding-first-run-cta">
            Open {agent.name} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
